import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { usePageSEO } from '../../hooks/usePageSEO';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData } from '../../utils/structuredData';

export default function FxBasicsPage() {
  // SEO設定
  usePageSEO({
    title: 'FXとは？初心者向け超入門｜仕組みから始め方まで完全解説',
    description: 'FX（外国為替証拠金取引）の基本を初心者向けに徹底解説。仕組み、レバレッジ、スプレッド、リスク管理など、FX取引に必要な知識を分かりやすく説明します。図解と具体例で理解しやすい入門ガイド。',
    keywords: 'FX, 外国為替, 初心者, 入門, 仕組み, レバレッジ, スプレッド, 始め方',
    ogImage: 'https://public.readdy.ai/ai/img_res/8edbe82a3af017ad552b5dadb2994581.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'FXとは？初心者向け超入門',
        description: 'FX取引の基本から仕組み、リスク管理まで初心者向けに分かりやすく解説。レバレッジ、スプレッド、通貨ペアなどの基礎知識と、安全な始め方を詳しく説明します。',
        image: 'https://readdy.ai/api/search-image?query=FX%20basics&width=1200&height=630&seq=basics-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: '/fx-basics'
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'FX入門', url: '/fx-basics' }
      ]),
      createFAQStructuredData([
        {
          question: 'FXとは何ですか？',
          answer: 'FX（Foreign Exchange）とは、外国為替証拠金取引のことです。異なる国の通貨を売買することで、為替レートの変動から利益を得る投資方法です。例えば、1ドル=100円のときに1万ドルを購入し、1ドル=110円になったときに売却すると、10万円の利益となります。'
        },
        {
          question: 'FXはいくらから始められますか？',
          answer: 'DMM FXでは、レバレッジを活用することで少額から取引を始められます。最小取引単位は1万通貨で、レバレッジ25倍を使えば、米ドル/円の場合、約6万円の証拠金で取引可能です。ただし、初心者の方は余裕を持った資金で、低いレバレッジから始めることをおすすめします。'
        },
        {
          question: 'FXのレバレッジとは何ですか？',
          answer: 'レバレッジとは「てこの原理」を意味し、少ない資金で大きな取引ができる仕組みです。日本国内のFX取引では最大25倍のレバレッジが認められています。例えば、10万円の資金でレバレッジ25倍を使えば、250万円分の取引が可能になります。ただし、レバレッジは利益も損失も拡大させるため、適切なリスク管理が必要です。'
        },
        {
          question: 'FX取引のリスクは何ですか？',
          answer: 'FX取引の主なリスクは、為替変動リスク、レバレッジリスク、ロスカットリスク、感情的な取引リスクがあります。予想と反対方向に相場が動くと損失が発生し、レバレッジが高いほど損失も拡大します。対策として、損切りラインを事前に設定する、低いレバレッジから始める、余裕を持った証拠金で取引する、取引ルールを決めて守ることが重要です。'
        },
        {
          question: 'FXは初心者でも始められますか？',
          answer: 'はい、初心者でも始められます。DMM FXでは24時間サポート体制、デモトレード、初心者向けセミナーなど、充実したサポートを提供しています。最初は少額から始め、デモトレードで練習し、取引ルールを決めて守ることで、安全に取引を始められます。また、FXの基礎知識を学び、リスク管理を徹底することが成功への近道です。'
        }
      ])
    ]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
      <p className="text-sm text-gray-500 mt-3">口座開設・維持費無料 / 最短5分で申込完了</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">FXとは?初心者向け超入門</h1>
            <p className="text-xl text-teal-50 mb-8 max-w-3xl mx-auto">
              FX取引の基本から仕組み、リスク管理まで分かりやすく解説します
            </p>
            
            {/* CTA①：冒頭 */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              <i className="ri-gift-line"></i>
              <span>👉 今だけ最大50万円キャッシュバック</span>
            </div>
            <div className="block mb-3">
              <a 
                href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
                rel="nofollow"
                className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                DMM FXで始める
              </a>
              <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
            </div>
            <p className="text-sm text-teal-100">口座開設・維持費無料 / 最短5分で申込完了</p>
          </div>
        </div>
      </section>

      {/* FXとは Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">FXとは何か?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              FX（Foreign Exchange）とは、外国為替証拠金取引のことです。異なる国の通貨を売買することで、為替レートの変動から利益を得る投資方法です。
            </p>
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">具体例で理解する</h3>
              <p className="text-gray-700 leading-relaxed">
                例えば、1ドル=100円のときに1万ドルを購入（100万円）し、1ドル=110円になったときに売却すると、110万円になります。この差額の10万円が利益となります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 取引事例図解 Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-teal-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">FX取引の流れを図解で理解</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            実際の取引がどのように行われるか、具体的な事例で見てみましょう
          </p>

          {/* 買いで利益を出す事例 */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-arrow-up-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">事例1：円安で利益を出す（買いポジション）</h3>
                <p className="text-green-600 font-semibold">ドルを買って、円安になったら売る</p>
              </div>
            </div>

            {/* 図解フロー */}
            <div className="grid md:grid-cols-5 gap-4 items-center mb-8">
              {/* Step 1 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">1</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">ドルを購入</p>
                <p className="text-xs text-gray-600">1ドル = 150円</p>
                <p className="text-lg font-bold text-teal-600 mt-2">1万ドル購入</p>
                <p className="text-sm text-gray-500">（150万円）</p>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex justify-center">
                <i className="ri-arrow-right-line text-3xl text-teal-400"></i>
              </div>

              {/* Step 2 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">2</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">為替が変動</p>
                <p className="text-xs text-gray-600">円安が進行</p>
                <div className="flex items-center justify-center mt-2">
                  <span className="text-gray-500">150円</span>
                  <i className="ri-arrow-right-line text-amber-500 mx-2"></i>
                  <span className="text-amber-600 font-bold">155円</span>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex justify-center">
                <i className="ri-arrow-right-line text-3xl text-teal-400"></i>
              </div>

              {/* Step 3 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border-2 border-green-300">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">3</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">ドルを売却</p>
                <p className="text-xs text-gray-600">1ドル = 155円</p>
                <p className="text-lg font-bold text-green-600 mt-2">155万円に！</p>
                <p className="text-sm text-green-500 font-semibold">+5万円の利益</p>
              </div>
            </div>

            {/* 計算式 */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-calculator-line text-teal-500 mr-2"></i>
                利益計算
              </h4>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">売却額</p>
                  <p className="text-xl font-bold text-gray-900">155万円</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">購入額</p>
                  <p className="text-xl font-bold text-gray-900">150万円</p>
                </div>
                <div className="bg-green-100 rounded-lg p-4">
                  <p className="text-sm text-green-600 mb-1">利益</p>
                  <p className="text-xl font-bold text-green-600">+5万円</p>
                </div>
              </div>
            </div>
          </div>

          {/* 売りで利益を出す事例 */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-arrow-down-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">事例2：円高で利益を出す（売りポジション）</h3>
                <p className="text-rose-600 font-semibold">ドルを売って、円高になったら買い戻す</p>
              </div>
            </div>

            {/* 図解フロー */}
            <div className="grid md:grid-cols-5 gap-4 items-center mb-8">
              {/* Step 1 */}
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">1</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">ドルを売却</p>
                <p className="text-xs text-gray-600">1ドル = 150円</p>
                <p className="text-lg font-bold text-rose-600 mt-2">1万ドル売却</p>
                <p className="text-sm text-gray-500">（150万円相当）</p>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex justify-center">
                <i className="ri-arrow-right-line text-3xl text-rose-400"></i>
              </div>

              {/* Step 2 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">2</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">為替が変動</p>
                <p className="text-xs text-gray-600">円高が進行</p>
                <div className="flex items-center justify-center mt-2">
                  <span className="text-gray-500">150円</span>
                  <i className="ri-arrow-right-line text-amber-500 mx-2"></i>
                  <span className="text-amber-600 font-bold">145円</span>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex justify-center">
                <i className="ri-arrow-right-line text-3xl text-rose-400"></i>
              </div>

              {/* Step 3 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border-2 border-green-300">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">3</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">ドルを買い戻し</p>
                <p className="text-xs text-gray-600">1ドル = 145円</p>
                <p className="text-lg font-bold text-green-600 mt-2">145万円で決済</p>
                <p className="text-sm text-green-500 font-semibold">+5万円の利益</p>
              </div>
            </div>

            {/* ポイント解説 */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-lightbulb-line text-rose-500 mr-2"></i>
                売りから入れるのがFXの魅力
              </h4>
              <p className="text-gray-700 leading-relaxed">
                株式投資では基本的に「安く買って高く売る」しかできませんが、FXでは「高く売って安く買い戻す」ことも可能です。つまり、<strong className="text-rose-600">円高でも円安でも利益を狙える</strong>のがFXの大きな特徴です。
              </p>
            </div>
          </div>

          {/* 成功事例 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              <i className="ri-trophy-line text-amber-500 mr-2"></i>
              初心者の成功事例
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* 事例1 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-smile-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Aさん（30代会社員）</p>
                    <p className="text-sm text-gray-600">FX歴：6ヶ月</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-500 mr-2 mt-1"></i>
                    <p className="text-gray-700 text-sm">初期資金<strong>10万円</strong>からスタート</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-500 mr-2 mt-1"></i>
                    <p className="text-gray-700 text-sm">レバレッジは<strong>5倍以下</strong>に抑えて運用</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-500 mr-2 mt-1"></i>
                    <p className="text-gray-700 text-sm">米ドル/円のみに集中</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-teal-200">
                  <p className="text-sm text-gray-600 mb-1">6ヶ月後の成果</p>
                  <p className="text-2xl font-bold text-teal-600">+12万円（+120%）</p>
                </div>
                <div className="mt-4 bg-white rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <i className="ri-double-quotes-l text-teal-400"></i>
                    焦らず少額から始めたのが良かった。DMM FXのアプリが使いやすくて助かりました。
                    <i className="ri-double-quotes-r text-teal-400"></i>
                  </p>
                </div>
              </div>

              {/* 事例2 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-heart-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Bさん（40代主婦）</p>
                    <p className="text-sm text-gray-600">FX歴：1年</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <i className="ri-check-line text-amber-500 mr-2 mt-1"></i>
                    <p className="text-gray-700 text-sm">デモトレードで<strong>3ヶ月</strong>練習</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-amber-500 mr-2 mt-1"></i>
                    <p className="text-gray-700 text-sm">損切りを徹底</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-amber-500 mr-2 mt-1"></i>
                    <p className="text-gray-700 text-sm">1日30分の<strong>スキマ時間</strong>で取引</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <p className="text-sm text-gray-600 mb-1">1年後の成果</p>
                  <p className="text-2xl font-bold text-amber-600">+25万円（月平均+2万円）</p>
                </div>
                <div className="mt-4 bg-white rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <i className="ri-double-quotes-l text-amber-400"></i>
                    家事の合間にスマホで取引。24時間取引できるのが主婦には嬉しいです。
                    <i className="ri-double-quotes-r text-amber-400"></i>
                  </p>
                </div>
              </div>
            </div>

            {/* 成功のポイント */}
            <div className="mt-8 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 text-white">
              <h4 className="font-bold text-lg mb-4 flex items-center">
                <i className="ri-key-2-line mr-2"></i>
                成功者に共通する3つのポイント
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/20 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mr-2">
                    <span className="font-bold">1</span>
                  </div>
                  <p className="font-semibold">少額から始める</p>
                  <p className="text-sm text-teal-100 mt-1">最初は10万円以下で</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mr-2">
                    <span className="font-bold">2</span>
                  </div>
                  <p className="font-semibold">損切りを徹底</p>
                  <p className="text-sm text-teal-100 mt-1">ルールを決めて守る</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mr-2">
                    <span className="font-bold">3</span>
                  </div>
                  <p className="font-semibold">焦らず継続</p>
                  <p className="text-sm text-teal-100 mt-1">長期目線で取り組む</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FXの特徴 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">FX取引の5つの特徴</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">24時間取引可能</h3>
              <p className="text-gray-600 leading-relaxed">
                平日であれば24時間いつでも取引できます。株式市場と違い、自分のライフスタイルに合わせて取引時間を選べます。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-arrow-up-down-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">少額から始められる</h3>
              <p className="text-gray-600 leading-relaxed">
                レバレッジを活用することで、少ない資金でも大きな取引が可能です。DMM FXなら最小1万通貨から取引できます。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-exchange-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">売りからも入れる</h3>
              <p className="text-gray-600 leading-relaxed">
                円高でも円安でも利益を狙えます。「買い」だけでなく「売り」からも取引を始められるのがFXの魅力です。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">スワップポイント</h3>
              <p className="text-gray-600 leading-relaxed">
                2国間の金利差によって、ポジションを保有しているだけで毎日利益を得られる可能性があります。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">透明性の高い市場</h3>
              <p className="text-gray-600 leading-relaxed">
                世界中で取引されているため、価格操作が難しく、公平な取引環境が保たれています。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-smartphone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">スマホで完結</h3>
              <p className="text-gray-600 leading-relaxed">
                口座開設から取引まで、すべてスマートフォンで完結できます。外出先でも気軽に取引可能です。
              </p>
            </div>
          </div>

          {/* DMM FX評判へのリンク */}
          <div className="mt-12 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-chat-quote-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">DMM FXの評判を確認</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  実際の利用者の口コミ・評判、メリット・デメリット、他社との比較については、こちらの記事で詳しくまとめています。
                </p>
                <Link 
                  to="/reviews" 
                  className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700 underline cursor-pointer text-base"
                >
                  <span>→ DMM FXの口コミ・評判を詳しく見る</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FXの仕組み Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">FX取引の仕組み</h2>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">通貨ペアとは</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              FXでは2つの通貨を組み合わせて取引します。例えば「米ドル/円」は、米ドルを円で売買することを意味します。
            </p>
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">主要な通貨ペア</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">米ドル/円（USD/JPY）</span>
                  <span className="text-gray-600">最も取引量が多い</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">ユーロ/円（EUR/JPY）</span>
                  <span className="text-gray-600">初心者にも人気</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">ポンド/円（GBP/JPY）</span>
                  <span className="text-gray-600">値動きが大きい</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">豪ドル/円（AUD/JPY）</span>
                  <span className="text-gray-600">スワップ狙いに人気</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">レバレッジとは</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              レバレッジとは「てこの原理」のことで、少ない資金で大きな取引ができる仕組みです。日本では最大25倍のレバレッジが認められています。
            </p>
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">レバレッジの例</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                10万円の証拠金で、レバレッジ25倍を使えば250万円分の取引が可能になります。
              </p>
              <p className="text-sm text-gray-600">
                ※レバレッジは利益も損失も拡大するため、リスク管理が重要です
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">スプレッドとは</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              スプレッドとは、買値と売値の差額のことで、FX会社の実質的な手数料となります。スプレッドが狭いほど、取引コストが安くなります。
            </p>
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">DMM FXのスプレッド例</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900">米ドル/円</span>
                  <span className="font-bold text-teal-600">0.2銭</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900">ユーロ/円</span>
                  <span className="font-bold text-teal-600">0.4銭</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900">ポンド/円</span>
                  <span className="font-bold text-teal-600">0.9銭</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">※原則固定（例外あり）</p>
            </div>
          </div>
        </div>
      </section>

      {/* リスク管理 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">FX取引のリスクと対策</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">為替変動リスク</h3>
                  <p className="text-gray-600 leading-relaxed">
                    予想と反対方向に相場が動くと損失が発生します。
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-teal-900 mb-2">対策</p>
                <p className="text-sm text-gray-700">
                  損切りラインを事前に設定し、損失を限定する
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-arrow-up-down-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">レバレッジリスク</h3>
                  <p className="text-gray-600 leading-relaxed">
                    レバレッジが高いほど、損失も拡大します。
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-teal-900 mb-2">対策</p>
                <p className="text-sm text-gray-700">
                  最初は低いレバレッジから始め、慣れてから調整する
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-funds-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ロスカットリスク</h3>
                  <p className="text-gray-600 leading-relaxed">
                    証拠金維持率が一定以下になると強制決済されます。
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-teal-900 mb-2">対策</p>
                <p className="text-sm text-gray-700">
                  余裕を持った証拠金で取引し、証拠金維持率を常に確認する
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-emotion-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">感情的な取引</h3>
                  <p className="text-gray-600 leading-relaxed">
                    損失を取り戻そうと無理な取引をしてしまう。
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-teal-900 mb-2">対策</p>
                <p className="text-sm text-gray-700">
                  取引ルールを決めて守る。冷静さを失ったら取引を休む
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-6">
            <div className="flex items-start">
              <i className="ri-information-line text-2xl text-amber-600 mr-4 flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">初心者の方へ</h4>
                <p className="text-gray-700 leading-relaxed">
                  FXは正しい知識とリスク管理があれば、有効な資産運用手段となります。最初は少額から始め、デモトレードで練習することをおすすめします。DMM FXでは初心者向けのサポート体制も充実しています。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 始め方 Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">FXの始め方</h2>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">口座開設</h3>
              <p className="text-sm text-gray-600">
                オンラインで簡単に申し込み
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">入金</h3>
              <p className="text-sm text-gray-600">
                クイック入金で即時反映
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">取引開始</h3>
              <p className="text-sm text-gray-600">
                スマホアプリで簡単取引
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">利益確定</h3>
              <p className="text-sm text-gray-600">
                出金も簡単スピーディー
              </p>
            </div>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mb-8 bg-white rounded-xl p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>

          {/* CTA③：始め方セクション */}
          <CTAButton />
        </div>
      </section>

      {/* 関連記事 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">関連記事</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/leverage" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-arrow-up-down-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">レバレッジ解説</h3>
              <p className="text-sm text-gray-600 mb-4">レバレッジの仕組みとリスク管理を詳しく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/economic-indicators" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bar-chart-grouped-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">経済指標の見方</h3>
              <p className="text-sm text-gray-600 mb-4">FX取引で重要な経済指標の活用方法</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/beginner-guide" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-star-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">初心者ガイド</h3>
              <p className="text-sm text-gray-600 mb-4">DMM FXは初心者でも安心して始められます</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
